<template lang="pug">
div
  ShowVendor(
    v-if="$route.query.vendor_id"
    :id="$route.query.vendor_id"
  )
  template(v-else)
    UiCardSection
      UiStack
        UiStackItem(fill)
          .ui-card-section-title {{ $t("companies.tabs.vendors") }}
    UiCardSection
      RestTable(
        namespace="company_vendors"
        :headers="headers"
        ref="companyVendorsTable"
        :fetch-handler="getData"
        :initial-filters="initialFilters"
        embed
      )
        template(#filters="{ filters, updateFilters }")
          UiStack
            UiStackItem(fill)
              UiFormGroup(
                :label="$t('actions.search')"
                label-for="name-input"
                label-cols="4"
              )
                UiInput#name-input(
                  :modelValue="filters.search"
                  type="search"
                  @update:modelValue="updateFilters({ search: $event })"
                )
            UiStackItem(fill)
              UiFormGroup(
                :label="$t('companies.automation.start_date')"
                label-for="start-date-input"
                label-cols="5"
              )
                UiDatePicker#start-date-input(
                  :modelValue="new Date(filters.start_date)"
                  type="date"
                  @update:modelValue="updateFilters({ start_date: startOfDay($event) })"
                )
            UiStackItem(fill)
              UiFormGroup(
                :label="$t('companies.automation.end_date')"
                label-for="end-date-input"
                label-cols="5"
              )
                UiDatePicker#end-date-input(
                  :modelValue="new Date(filters.end_date)"
                  type="date"
                  @update:modelValue="updateFilters({ end_date: endOfDay($event) })"
                )
          UiCardSection
            UiStack
              UiStackItem
                UiCheckbox#combine-all-switch(
                  v-model="combineAll"
                  switch
                  @update:modelValue="changeCombineAll(combineAll, 'combine_lines', $t(\"companies.vendors.combine_all\"))"
                ) {{ $t("companies.vendors.combine_all") }}
              UiStackItem
                UiCheckbox#combine-all-accounts-switch(
                  v-model="combineAllAccounts"
                  switch
                  @update:modelValue="changeCombineAll(combineAllAccounts, 'combine_lines_by_account', $t(\"companies.vendors.combine_all_accounts\"))"
                ) {{ $t("companies.vendors.combine_all_accounts") }}
              UiStackItem
                UiCheckbox#skip-proposed-account(
                  v-model="skipProposedAccount"
                  switch
                  @update:modelValue="changeCombineAll(skipProposedAccount, 'skip_proposed_account', $t(\"companies.vendors.skip_proposed_account\"))"
                ) {{ $t("companies.vendors.skip_proposed_account") }}
              UiStackItem
                UiCheckbox#skip-proposed-dimension(
                  v-model="skipProposedDimension"
                  switch
                  @update:modelValue="changeCombineAll(skipProposedDimension, 'skip_proposed_dimension', $t(\"companies.vendors.skip_proposed_dimension\"))"
                ) {{ $t("companies.vendors.skip_proposed_dimension") }}
        template(#cell:name="{ item }")
          RouterLink(
            :to="{ ...$route, query: { ...$route.query, vendor_id: item.id.toString() } }"
            label="item.name"
          ) {{ item.name }}
        template(#cell:specified_vat_code="{ item }")
          UiSelect(
            v-model="item.specified_vat_code"
            :options="vatOptions"
            labelKey="text"
            @update:modelValue="changeVatCode(item)"
            :canDeselect="false"
            :canClear="false"
          )
        template(#cell:combine_lines="{ item }")
          UiCheckbox(
            v-model="item.combine_lines"
            switch
            @update:modelValue="changeCombine(item)"
          )
        template(#cell:combine_lines_by_account="{ item }")
          UiCheckbox(
            v-model="item.combine_lines_by_account"
            switch
            @update:modelValue="changeCombineAccount(item)"
          )
        template(#cell:suggest_voucher_date="{ item }")
          UiCheckbox(
            v-model="item.suggest_voucher_date"
            switch
            @update:modelValue="changeSuggestVoucherDate(item)"
          )
        template(#cell:skip_proposed_account="{ item }")
          UiCheckbox(
            v-model="item.skip_proposed_account"
            switch
            @update:modelValue="changeSkipProposedAccount(item)"
          )
        template(#cell:skip_proposed_dimension="{ item }")
          UiCheckbox(
            v-model="item.skip_proposed_dimension"
            switch
            @update:modelValue="changeSkipProposedDimension(item)"
          )
        template(#cell:is_accounted="{ item }")
          UiSelect(
            v-model="item.is_accounted"
            :options="accountedOptions"
            labelKey="text"
            @update:modelValue="changeAccounted(item)"
            :canDeselect="false"
            :canClear="false"
          )
        template(#cell:default_account="{ item }")
          UiInput(
            type="number"
            v-model="item.default_account"
            @input="setEdit(item)"
            @update:modelValue="changeDefaultAccountNumber(item)"
            inputSize="20"
          )
        template(#cell:vendor_dimensions="{ item }")
          UiButton(
            size="sm"
            variant="smooth-info"
            @click="dimensionsModalOpen(item)"
          ) {{ $t("companies.vendors.vendor_dimensions") }}
        template(#cell:replacement_codes="{ item }")
          UiButton(
            size="sm"
            variant="smooth-info"
            @click="replacementModalOpen(item)"
          ) {{ $t("companies.vendors.replacement_account_list") }}
    UiCardSection
      UiButton(
        v-if="edited_items.length > 0"
        variant="primary"
        @click="saveAll"
        icon="Check"
      ) Save vendors
  EditVendorDimensionsModal(
    ref="EditVendorDimensionsModal"
    @done="reload"
  )
  EditReplacementAccountListModal(
    ref="EditReplacementModal"
    @done="reload"
  )
</template>

<script>
import router from "@/router";
import ApiCompanyVendors from "@/api/companyVendors.js";
import EditVendorDimensionsModal from "@/modals/Companies/editVendorDimensions.vue";
import ShowVendor from "@/views/Vendor/show.vue";
import EditReplacementAccountListModal from "@/modals/Companies/editReplacementAccountList.vue";
import { addYears } from "@/common/date_utils";
import { endOfDay, startOfDay } from "@bi-book/bibook-ui-kit";

export default {
  components: {
    EditVendorDimensionsModal,
    ShowVendor,
    EditReplacementAccountListModal,
  },

  data() {
    const startDate =
      typeof this.$route.query.purchaseInvoiceStartDate !== "undefined"
        ? new Date(this.$route.query.purchaseInvoiceStartDate)
        : addYears(new Date(), -1);
    const endDate =
      typeof this.$route.query.purchaseInvoiceEndDate !== "undefined"
        ? new Date(this.$route.query.purchaseInvoiceEndDate)
        : new Date();

    return {
      initialFilters: {
        start_date: startOfDay(startDate).toISOString(),
        end_date: endOfDay(endDate).toISOString(),
      },
      headers: [
        {
          key: "id",
          label: "#",
          squeeze: true,
          cellClass: "text-nowrap",
        },
        {
          key: "name",
          label: "Name",
          sortable: true,
        },
        {
          key: "business_id",
          label: "Business ID",
          cellClass: "text-nowrap",
        },
        {
          key: "account_number",
          label: "Account Number",
        },
        {
          key: "combine_lines",
          label: "Combine lines",
          sortable: true,
        },
        {
          key: "combine_lines_by_account",
          label: "Combine lines by account number",
          sortable: true,
        },
        {
          key: "suggest_voucher_date",
          label: "Suggest voucher date",
          sortable: true,
        },
        {
          key: "skip_proposed_account",
          label: "Skip proposed account",
          sortable: true,
        },
        {
          key: "skip_proposed_dimension",
          label: "Skip proposed dimension",
          sortable: true,
        },
        {
          key: "default_account",
          label: "Default Account number",
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "specified_vat_code",
          label: "VAT code",
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "is_accounted",
          label: "Automate accounting",
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "invoices_amount",
          label: "Invoice Amount",
          sortable: true,
        },
        {
          key: "suggested_correct_accounts",
          label: "Suggested correct Account %",
          squeeze: false,
        },
        {
          key: "suggested_correct_dimensions",
          label: "Suggested correct Dimensions %",
        },
        {
          key: "vendor_dimensions",
          label: "Vendor Dimensions",
        },
        {
          key: "replacement_codes",
          label: "Replacement codes",
        },
      ],
      items: [],
      edited_items: [],
      combineAll: false,
      combineAllAccounts: false,
      skipProposedAccount: false,
      skipProposedDimension: false,
      accountedOptions: [
        { value: "company_setting", text: "Company setting" },
        { value: "suggest_automate", text: "Suggest and automate" },
        { value: "only_suggest", text: "Only suggest" },
        { value: "donot_suggest", text: "Don't suggest" },
      ],
      vatOptions: [],
    };
  },

  methods: {
    startOfDay: startOfDay,
    endOfDay: endOfDay,
    getMostCommonVatCodes() {
      ApiCompanyVendors.mostCommonVatCodes(this.$route.params.id).then(
        (resp) => {
          this.vatOptions = resp.data.codes;
          this.vatOptions.unshift({ text: "", value: "" });
        },
      );
    },
    reload() {
      this.$refs.companyVendorsTable.fetchData();

      this.$router.push({
        query: {
          ...this.$route.query,
          purchaseInvoiceStartDate:
            document.querySelector("#start-date-input").value,
          purchaseInvoiceEndDate:
            document.querySelector("#end-date-input").value,
        },
      });
    },
    getData(params, config) {
      return ApiCompanyVendors.fetchVendors(
        this.$route.params.id,
        params,
        config,
      ).then((resp) => {
        this.items = resp.data.vendors;
        return {
          items: resp.data.vendors,
          meta: resp.data.meta,
        };
      });
    },
    changeCombine(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { combine_lines: item.combine_lines },
      }).then((resp) => {
        item = resp.data.vendor;
      });
    },
    changeCombineAccount(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { combine_lines_by_account: item.combine_lines_by_account },
      }).then((resp) => {
        item = resp.data.vendor;
      });
    },
    changeAccounted(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { is_accounted: item.is_accounted },
      }).then((resp) => {});
    },
    changeVatCode(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { specified_vat_code: item.specified_vat_code },
      }).then((resp) => {
        item.is_edited = false;
      });
    },
    changeSuggestVoucherDate(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { suggest_voucher_date: item.suggest_voucher_date },
      }).then((resp) => {
        item.is_edited = false;
      });
    },
    changeSkipProposedAccount(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { skip_proposed_account: item.skip_proposed_account },
      }).then((resp) => {
        item = resp.data.vendor;
      });
    },
    changeSkipProposedDimension(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { skip_proposed_dimension: item.skip_proposed_dimension },
      }).then((resp) => {
        item = resp.data.vendor;
      });
    },
    changeDefaultAccountNumber(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { default_account: item.default_account },
      }).then((resp) => {
        item.is_edited = false;
      });
    },
    changeApprover(item) {
      ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
        vendor: { approver_id: item.approver_id },
      }).then((resp) => {
        item.is_edited = false;
      });
    },
    save(item) {
      item.is_edited = false;
      return new Promise((res) => {
        ApiCompanyVendors.updateVendor(this.$route.params.id, item.id, {
          vendor: {
            default_account: item.default_account,
            specified_vat_code: item.specified_vat_code,
            is_accounted: item.is_accounted,
          },
        }).then((resp) => {
          item.is_edited = false;
        });
      });
    },

    saveAll() {
      this.items.forEach((item) => {
        if (item.is_edited) {
          this.save(item);
        }
      });
      this.edited_items = [];
    },

    changeCombineAll(combine, field, title) {
      this.$dialogConfirm({
        title: title,
        description: this.$t("companies.vendors.description"),
        variant: "smooth-secondary",
        icon: "pencil",
        okLabel: this.$t("actions.ok"),
        okVariant: "primary",
        okIcon: "pencil",
        cancelVariant: "smooth-secondary",
      }).then((result) => {
        if (!result) {
          this.combineAll = false;
          return;
        }

        let request;

        let state = { field: field, value: combine };

        request = ApiCompanyVendors.switchCombineLines(
          this.$route.params.id,
          state,
        );

        request.then(() => this.reload());
      });
    },
    goBack() {
      router.push("/companies");
    },
    dimensionsModalOpen(item) {
      this.$refs.EditVendorDimensionsModal.open(this.$route.params.id, item);
    },
    replacementModalOpen(item) {
      this.$refs.EditReplacementModal.open(this.$route.params.id, item);
    },
    setEdit(item) {
      item.is_edited = true;
      this.edited_items.push(item.id);
    },
  },

  mounted() {
    this.getMostCommonVatCodes();
  },
};
</script>

<style scoped>
.table-input {
  width: 300px;
}
</style>
